import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import { TextField } from "../../../../ui/components/TextField";
import {
  FeatherClock,
  FeatherEdit,
  FeatherPause,
  FeatherPlay,
  FeatherPlus,
  FeatherRefreshCw,
  FeatherTrash2,
  FeatherX,
} from "@subframe/core";
import {
  listCronJobs,
  createCronJob,
  updateCronJob,
  deleteCronJob,
  runCronJob,
  CronJobDTO,
} from "../../data/api/cronApi";

const emptyForm = { name: "", url: "", schedule: "*/5 * * * *", timeout_s: "300" };

export const CronsPage: React.FC = () => {
  const [jobs, setJobs] = useState<CronJobDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<number | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await listCronJobs();
      setJobs(res.data?.jobs ?? []);
    } catch (err: any) {
      setError(err.message || "Failed to load cron links");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (job: CronJobDTO) => {
    setEditingId(job.id);
    setForm({
      name: job.name,
      url: job.url,
      schedule: job.schedule,
      timeout_s: String(job.timeout_s ?? 300),
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.url.trim() || !form.schedule.trim()) {
      setFormError("Name, URL and schedule are required.");
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      setSuccessMsg(null);
      const payload = {
        name: form.name.trim(),
        url: form.url.trim(),
        schedule: form.schedule.trim(),
        timeout_s: parseInt(form.timeout_s, 10) || 300,
      };
      if (editingId === null) {
        await createCronJob(payload);
        setSuccessMsg(`Cron link "${payload.name}" created — it will run on schedule.`);
      } else {
        await updateCronJob(editingId, payload);
        setSuccessMsg(`Cron link "${payload.name}" updated.`);
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditingId(null);
      await fetchJobs();
    } catch (err: any) {
      setFormError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (job: CronJobDTO) => {
    try {
      setBusyId(job.id);
      setError(null);
      await updateCronJob(job.id, { enabled: !job.enabled });
      await fetchJobs();
    } catch (err: any) {
      setError(err.message || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleRun = async (job: CronJobDTO) => {
    try {
      setBusyId(job.id);
      setError(null);
      setSuccessMsg(null);
      const res = await runCronJob(job.id);
      const r = res.data.result;
      setSuccessMsg(
        r.status === "success"
          ? `"${job.name}" ran OK (${r.http_code ? `HTTP ${r.http_code}, ` : ""}${r.duration_ms}ms).`
          : r.status === "skipped"
          ? `"${job.name}" is already running — try again shortly.`
          : `"${job.name}" failed: ${r.error}`
      );
      await fetchJobs();
    } catch (err: any) {
      setError(err.message || "Run failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (job: CronJobDTO) => {
    if (!window.confirm(`Delete cron link "${job.name}"?`)) return;
    try {
      setBusyId(job.id);
      setError(null);
      await deleteCronJob(job.id);
      await fetchJobs();
    } catch (err: any) {
      setError(err.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex h-screen w-full flex-col bg-default-background">
      <div className="flex w-full flex-col items-start gap-6 p-12">
        {/* Header */}
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Cron Links
            </span>
            <span className="text-body font-body text-subtext-color">
              URLs hit automatically on a schedule (checked every minute, IST).
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-secondary"
              icon={<FeatherRefreshCw />}
              onClick={fetchJobs}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              icon={showForm ? <FeatherX /> : <FeatherPlus />}
              onClick={() => (showForm ? setShowForm(false) : openCreate())}
            >
              {showForm ? "Cancel" : "Add Cron Link"}
            </Button>
          </div>
        </div>

        {/* Success banner */}
        {successMsg && (
          <div className="flex w-full flex-col items-start gap-1 rounded-lg border border-solid border-green-200 bg-green-50 p-4">
            <span className="text-sm font-medium text-green-800">{successMsg}</span>
          </div>
        )}

        {/* Inline add/edit form */}
        {showForm && (
          <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {editingId === null ? "New cron link" : "Edit cron link"}
            </span>
            <div className="flex w-full max-w-2xl flex-col items-start gap-3">
              <TextField
                className="w-full"
                variant="outline"
                label="Name"
                icon={<FeatherClock />}
              >
                <TextField.Input
                  placeholder="zoho-sync-dev"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </TextField>
              <TextField
                className="w-full"
                variant="outline"
                label="URL"
                helpText='Path starting with "/" runs on EVERY active tenant (e.g. /backend/api/notifications/process-queue?limit=50). Full https:// URL runs once, as given.'
              >
                <TextField.Input
                  placeholder="/backend/… or https://…"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                />
              </TextField>
              <div className="flex w-full items-start gap-3">
                <TextField
                  className="grow"
                  variant="outline"
                  label="Schedule (cron expression)"
                  helpText='e.g. "*/5 * * * *" = every 5 min, "0 2 * * *" = daily 2:00 IST'
                >
                  <TextField.Input
                    placeholder="*/5 * * * *"
                    value={form.schedule}
                    onChange={(e) => setForm({ ...form, schedule: e.target.value })}
                  />
                </TextField>
                <TextField
                  className="w-40"
                  variant="outline"
                  label="Timeout (s)"
                >
                  <TextField.Input
                    placeholder="300"
                    value={form.timeout_s}
                    onChange={(e) => setForm({ ...form, timeout_s: e.target.value })}
                  />
                </TextField>
              </div>
              {formError && (
                <span className="text-sm font-medium text-error-700">{formError}</span>
              )}
            </div>
            <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
              {editingId === null ? "Create" : "Save"}
            </Button>
          </div>
        )}

        {/* List error */}
        {error && (
          <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
            <span className="text-sm font-medium text-red-800">{error}</span>
          </div>
        )}

        {/* Loading */}
        {loading && jobs.length === 0 ? (
          <div className="flex w-full items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading cron links...</p>
            </div>
          </div>
        ) : (
          <div className="flex w-full flex-col items-start gap-4">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>URL</Table.HeaderCell>
                  <Table.HeaderCell>Schedule</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Last run</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {jobs.map((job) => (
                <Table.Row key={job.id}>
                  <Table.Cell>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {job.name}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      className="text-body font-body text-subtext-color max-w-xs truncate"
                      title={job.url}
                    >
                      {job.url}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font font-mono">
                      {job.schedule}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant={job.enabled ? "success" : "neutral"}>
                      {job.enabled ? "Enabled" : "Paused"}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col items-start gap-1">
                      {job.last_run_at ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={job.last_status === "success" ? "success" : "error"}
                            >
                              {job.last_status === "success"
                                ? `OK ${job.last_http_code ?? ""}`
                                : "Failed"}
                            </Badge>
                            <span className="text-caption font-caption text-subtext-color">
                              {job.last_run_at}
                              {job.last_duration_ms != null
                                ? ` · ${job.last_duration_ms}ms`
                                : ""}
                            </span>
                          </div>
                          {job.last_status === "failed" && job.last_error && (
                            <span
                              className="text-caption font-caption text-error-700 max-w-xs truncate"
                              title={job.last_error}
                            >
                              {job.last_error}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-caption font-caption text-subtext-color">
                          Never ran
                        </span>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherPlay />}
                        onClick={() => handleRun(job)}
                        loading={busyId === job.id}
                        disabled={busyId === job.id}
                      >
                        Run
                      </Button>
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={job.enabled ? <FeatherPause /> : <FeatherPlay />}
                        onClick={() => handleToggle(job)}
                        disabled={busyId === job.id}
                      >
                        {job.enabled ? "Pause" : "Enable"}
                      </Button>
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherEdit />}
                        onClick={() => openEdit(job)}
                        disabled={busyId === job.id}
                      />
                      <Button
                        variant="destructive-tertiary"
                        size="small"
                        icon={<FeatherTrash2 />}
                        onClick={() => handleDelete(job)}
                        disabled={busyId === job.id}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>

            {!loading && jobs.length === 0 && (
              <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-neutral-border py-12">
                <span className="text-body font-body text-subtext-color">
                  No cron links yet. Click "Add Cron Link" to schedule one.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
